import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { resendVerificationEmail, verifyOtp } from "api";
import Backdrop from "components/global/Backdrop";
import Button from "components/global/Button";
import Logo from "components/global/Logo";
import CloseIcon from "icons/CloseIcon";
import styled from "styled-components";
import { showAlert } from "utils/utils";

const VerifyWrapper = styled(Backdrop)`
  .modal {
    width: 480px;
    background: #fff;
    border-radius: 8px;

    .contentContainer {
      width: 480px;
      gap: 24px;
      padding: 24px;
      border-radius: 8px;

      .pinInput {
        display: flex;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
        gap: 12px;
      }

      input {
        width: 56px;
        height: 72px;
        border-radius: 8px;
        border: solid #e2e2ea;
        padding: 23px 16px;
        font-size: 20px;
        font-weight: 450;
        line-height: 26px;

        &.error {
          border: 1px solid #fd0403;
        }
      }

      .buttonContainer {
        gap: 24px;

        .btn {
          width: 100%;
        }
      }
    }
  }
`;

const VerifyOtpModal = ({ user, setShowModal }) => {
  const [otp, setOTP] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState(false);
  const inputRefs = useRef([]);

  const handleInputChange = (index, value) => {
    const newOTP = [...otp];
    newOTP[index] = value;
    setOTP(newOTP);
    // Move to the next input box if current input is not empty
    if (value && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("Text");
    const otpRegex = /\d{6}/;
    const extractedOTP = pastedData.match(otpRegex);
    if (extractedOTP) {
      const newOTP = extractedOTP[0].split("").slice(0, otp.length);
      setOTP(newOTP);

      inputRefs.current[newOTP.length]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && index > 0 && otp[index] === "") {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const { mutate: handleResend, isPending } = useMutation({
    mutationFn: () => resendVerificationEmail({ email: user.email }),
    onSuccess: () => {
      showAlert({
        msg: "Verification email sent successfully",
        type: "success",
      });
    },
    onError: (error) => {
      showAlert({
        msg: error.message ?? "An error occurred",
        type: "danger",
      });
    },
  });

  const { mutate: handleVerifyOtp, isPending: isVerifying } = useMutation({
    mutationFn: () => verifyOtp({ code: otp.join("") }),
    onSuccess: async (res) => {
      showAlert({
        msg: res?.data?.message ?? "Email verified successfully",
        type: "success",
      });
      setShowModal(false);
    },
    onError: (error) => {
      showAlert({
        msg:
          error?.response?.data?.message ??
          error?.message ??
          "An error occurred",
        type: "danger",
      });
      setError(true);
    },
  });

  return (
    <VerifyWrapper className="show flexRow alignCenter justifyCenter">
      <div className="modal">
        <div className="contentContainer flexColumn alignCenter">
          <div className="fullWidth flexRow justifyEnd">
            <div className="cursorPointer" onClick={() => setShowModal(false)}>
              <CloseIcon />
            </div>
          </div>
          <Logo />
          <div className="flexColumn ">
            <h4 className="textCenter">Check your inbox</h4>
            <p className="l3 colorBodyText textCenter">
              We've sent a verification code via email to{" "}
              <span className="l3 bold">{user?.email}</span>, please enter this
              code to verify your account.
            </p>
          </div>
          <div className="buttonContainer flexColumn alignCenter justifyCenter spanFull">
            <div className="pinInput">
              {otp.map((value, index) => (
                <input
                  key={index}
                  placeholder=""
                  type="text"
                  name={`pin${index}`}
                  value={value}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  ref={(el) => (inputRefs.current[index] = el)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  maxLength={1}
                  onPaste={index === 0 ? handlePaste : null}
                  className={`textCenter ${error && "error"}`}
                  autoComplete="off"
                />
              ))}
            </div>
            <Button
              text="Verify email"
              bg="var(--secondary)"
              onClick={handleVerifyOtp}
              loading={isVerifying}
              fullWidth
            />
          </div>
          <p className="l3 textNoWrap">
            Didn't receive the code?{" "}
            <button
              type="button"
              onClick={() => handleResend()}
              disabled={isPending}
              className="colorSecondary cursorPointer"
            >
              Resend code
            </button>
          </p>
        </div>
      </div>
    </VerifyWrapper>
  );
};

export default VerifyOtpModal;
